import { useMemo, useState } from 'react';
import { useScheduleStore } from '@/stores/scheduleStore';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

export function MonthView() {
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  const { blocks } = useScheduleStore();

  // Generate calendar days
  const calendarDays = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();

    // First day of month
    const firstDay = new Date(year, month, 1);
    const startingDayOfWeek = firstDay.getDay();

    // Last day of month
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();

    // Days from previous month
    const prevMonthDays = startingDayOfWeek === 0 ? 6 : startingDayOfWeek - 1;
    const prevMonth = new Date(year, month, 0);
    const daysInPrevMonth = prevMonth.getDate();

    const days: Date[] = [];

    // Previous month days
    for (let i = prevMonthDays; i > 0; i--) {
      days.push(new Date(year, month - 1, daysInPrevMonth - i + 1));
    }

    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }

    // Next month days to fill the grid
    const remainingDays = 42 - days.length; // 6 weeks
    for (let i = 1; i <= remainingDays; i++) {
      days.push(new Date(year, month + 1, i));
    }

    return days;
  }, [currentMonth]);

  // Helper to format a Date as YYYY-MM-DD in local time
  const toLocalDateString = (d: Date) => {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  // Group blocks by day
  const blocksByDay = useMemo(() => {
    const grouped: Record<string, typeof blocks> = {};
    blocks.forEach(block => {
      const dateKey = toLocalDateString(new Date(block.startTime));
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(block);
    });
    return grouped;
  }, [blocks]);

  const goToPreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const goToToday = () => {
    const now = new Date();
    setCurrentMonth(new Date(now.getFullYear(), now.getMonth(), 1));
  };

  const monthName = currentMonth.toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="border-b p-2 sm:p-4">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-1 sm:gap-2">
            <Button variant="outline" size="sm" onClick={goToPreviousMonth}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={goToToday}>
              Today
            </Button>
            <Button variant="outline" size="sm" onClick={goToNextMonth}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          <h2 className="text-lg font-semibold sm:text-xl">{monthName}</h2>
        </div>
      </div>

      {/* Calendar grid */}
      <div className="flex-1 overflow-auto p-2 sm:p-4">
        <div className="grid h-full grid-cols-7 gap-1 sm:gap-2">
          {/* Day headers */}
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
            <div
              key={day}
              className="text-muted-foreground py-1 text-center text-xs font-medium sm:py-2 sm:text-sm"
            >
              {day}
            </div>
          ))}

          {/* Calendar days */}
          {calendarDays.map((day, idx) => {
            const dateKey = toLocalDateString(day);
            const dayBlocks = blocksByDay[dateKey] || [];
            const isCurrentMonth = day.getMonth() === currentMonth.getMonth();
            const isToday = day.toDateString() === new Date().toDateString();

            return (
              <div
                key={idx}
                className={cn(
                  'hover:bg-accent/50 min-h-[60px] cursor-pointer rounded-lg border p-1 text-xs transition-colors sm:min-h-[100px] sm:p-2 sm:text-sm',
                  !isCurrentMonth && 'opacity-40',
                  isToday && 'ring-primary ring-2'
                )}
              >
                <div className={cn('mb-1 font-medium sm:mb-2', isToday && 'text-primary')}>
                  {day.getDate()}
                </div>
                <div className="space-y-0.5 sm:space-y-1">
                  {dayBlocks.slice(0, 2).map(block => (
                    <div
                      key={block.id}
                      className="bg-primary/10 truncate rounded p-0.5 text-[10px] sm:p-1 sm:text-xs"
                    >
                      {block.title}
                    </div>
                  ))}
                  {dayBlocks.length > 2 && (
                    <Badge variant="secondary" className="text-[9px] sm:text-xs">
                      +{dayBlocks.length - 2}
                    </Badge>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
